import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Shared loading-skeleton primitives. Use them for the FIRST load of a content area (list, panel,
 * table, card), shaped like the final content so nothing jumps when data lands. See
 * `docs/code-standards.md` → "Loading states" for when to skeleton vs keep-previous vs stay silent.
 *
 * Every piece is `aria-hidden` (decorative); wrap a skeleton in `SkeletonStatus` so assistive tech
 * hears one "Loading…" announcement instead of a pile of empty boxes.
 */

/**
 * Shared skeleton block tokens — same pulse, radius, and sunk fill the agency skeletons use, so every
 * loading placeholder in the app reads as one system. Pulse is disabled for reduced-motion users.
 */
export const SKELETON_BLOCK = 'animate-pulse rounded-[var(--r-md)] bg-clovie-sunk motion-reduce:animate-none';

/**
 * A bar that fills exactly one text line box, so placeholder rows match loaded rows to the pixel.
 * The app's type scale is custom (tailwind.config): `text-base` = 15px / 22.5px line,
 * `text-lg` = 17px / 23.8px line — a 16px bar plus these margins fills each line box.
 */
export const SKELETON_TEXT_LINE = 'h-4 my-[3.25px]';
export const SKELETON_TEXT_LG_LINE = 'h-4 my-[3.9px]';
/** `text-sm` (14px / 19.6px line) — uppercase eyebrows and small labels. */
export const SKELETON_TEXT_SM_LINE = 'h-3.5 my-[2.8px]';
/** Arbitrary `text-[17px]` titles, which inherit the body's 1.5 line height (25.5px line). */
export const SKELETON_TEXT_17_LINE = 'h-[17px] my-[4.25px]';
/** `text-base leading-6` body copy (24px line). */
export const SKELETON_TEXT_LEADING6_LINE = 'h-4 my-1';
/** `text-xl leading-7` card titles (20px / 28px line). */
export const SKELETON_TEXT_XL_LINE = 'h-5 my-1';

export interface SkeletonProps {
  /** Size / shape overrides (`h-4 w-32`, `size-10 rounded-full`, …). */
  className?: string;
}

/**
 * One placeholder block; defaults to a full-width text-line height, override size and shape via
 * `className`. Rendered as a block-level `span` so it is valid inside inline/phrasing parents too.
 */
export function Skeleton({ className }: SkeletonProps) {
  return <span aria-hidden="true" className={cn(SKELETON_BLOCK, 'block h-4 w-full', className)} />;
}

export interface SkeletonTextProps {
  /** Number of lines (default 3). */
  lines?: number;
  /** Width of the last line — a ragged edge reads as text, not a bar (default `w-2/3`). */
  lastLineClassName?: string;
  className?: string;
}

/** A paragraph of text lines; the last one is shorter. */
export function SkeletonText({ lines = 3, lastLineClassName = 'w-2/3', className }: SkeletonTextProps) {
  return (
    <div aria-hidden="true" className={cn('space-y-2', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} className={index === lines - 1 && lines > 1 ? lastLineClassName : undefined} />
      ))}
    </div>
  );
}

export interface SkeletonListRowsProps {
  /** Number of rows (default 3). */
  rows?: number;
  /** Text lines per row — match the real row so heights line up (default 2). */
  linesPerRow?: number;
  /** Leading round avatar per row. */
  withAvatar?: boolean;
  /** Classes for each row container (padding / border / radius of the real row). */
  rowClassName?: string;
  /**
   * Vertical gap between a row's lines (default `space-y-2`). Tune it so title (20px) + meta lines
   * (16px each) + gaps add up to the real row's text height.
   */
  lineGapClassName?: string;
  /** Classes for the list container (spacing between rows). */
  className?: string;
}

/**
 * Placeholder rows for a list. The first line of each row is a bolder, shorter "title"; following
 * lines are "meta" lines of varying width. Pass the real row's box classes via `rowClassName`.
 */
export function SkeletonListRows({
  rows = 3,
  linesPerRow = 2,
  withAvatar = false,
  rowClassName,
  lineGapClassName = 'space-y-2',
  className,
}: SkeletonListRowsProps) {
  const metaWidths = ['w-3/4', 'w-1/2', 'w-2/3'];
  return (
    <ul aria-hidden="true" className={cn('space-y-2', className)}>
      {Array.from({ length: rows }, (_, row) => (
        <li key={row} className={cn('flex items-center gap-3', rowClassName)}>
          {withAvatar && <Skeleton className="size-10 shrink-0 rounded-full" />}
          <div className={cn('min-w-0 flex-1', lineGapClassName)}>
            <Skeleton className="h-5 w-2/5" />
            {Array.from({ length: Math.max(0, linesPerRow - 1) }, (_, line) => (
              <Skeleton key={line} className={metaWidths[line % metaWidths.length]} />
            ))}
          </div>
        </li>
      ))}
    </ul>
  );
}

export interface SkeletonStatusProps {
  children: ReactNode;
  /** Screen-reader announcement (default "Loading…"). */
  label?: string;
  className?: string;
}

/** Live `role="status"` wrapper for a skeleton: announces `label` once, hides the decorative boxes. */
export function SkeletonStatus({ children, label = 'Loading…', className }: SkeletonStatusProps) {
  return (
    <div role="status" aria-live="polite" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/**
 * Classes for a list that is being refined (search / filter / range change) while it still shows the
 * previous rows (`placeholderData` → `isPlaceholderData`): dimmed, not blanked. Pair it with
 * `aria-busy={isRefining}` on the same element and a small spinner in the search box / header.
 */
export function refiningListClass(isRefining: boolean) {
  return cn('transition-opacity duration-150 motion-reduce:transition-none', isRefining && 'opacity-60');
}
